
// ─────────────────────────────────────────────────────────────────────────
// APPEARANCE MODEL — pure, no THREE / DOM. Learns "how does THIS observed
// pipe look in THIS point cloud" from the colours of points that already have
// STRONG independent geometric + surface evidence, and provides:
//   • per-point / per-section appearance SIMILARITY to that model,
//   • how DISCRIMINATIVE appearance is right now (pipe vs background, pipe vs
//     a competing candidate),
//   • confidence in the model itself,
//   • a conservative update rule.
//
// Design commitments (spec):
//   - NOT a pipe detector. Supporting evidence only. APPEARANCE MATCH IS NOT
//     PIPE IDENTITY.
//   - MULTIMODAL: a real pipe carries body colour + white/blue markings +
//     dark joints + shadow. Modelled as a small set of colour MODES, never as
//     mean RGB ± tolerance.
//   - Colour space: cr/cg chromaticity (material colour) + Y luminance
//     (lighting / shadow) kept separate, so a shadow on an orange pipe is a
//     luminance change, not a colour change.
//   - NO fixed domain colours (black=PE, orange=sewer, …). Everything is
//     learned from the observed pipe.
//   - NO feedback loop: only SUPPORTED-SAME-OBJECT observations may move the
//     authoritative model (the caller enforces which samples are passed to
//     `updateAppearanceModel`).
//   - Deterministic: leader clustering in a fixed sample order, no RNG.
// ─────────────────────────────────────────────────────────────────────────

export const APPEARANCE_DEFAULTS = Object.freeze({
	appMaxModes: 4,                 // body + up to 3 marking / joint / shadow modes
	appModeGateChroma: 0.045,       // new-mode gate: chroma distance (cr,cg units)
	appModeGateLum: 45,             // new-mode gate: luminance distance (0-255)
	appMinModeWeight: 0.04,         // a mode below this sample fraction is merged away
	appSimSigmaChroma: 0.05,        // per-point similarity kernel width, chroma
	appSimSigmaLum: 40,             // per-point similarity kernel width, luminance
	appChromaReliableY: 55,         // below this luminance, chromaticity is numerically
	                               // unreliable (R/(R+G+B) blows up near black) and its
	                               // weight in every distance is scaled down toward 0
	appPointMatchTau: 0.4,          // a point with similarity ≥ this "matches" the model
	appUpdateAlpha: 0.1,            // EWMA rate for conservative drift along the pipe
	appNewModeMinFrac: 0.12,        // spawn a new mode only if ≥ this fraction of an
	                               // update's samples form a coherent unassigned cluster
	appConfSectionsFull: 4,         // model confidence saturates at this many supported sections
	appConfPointsFull: 1500,        // … and this many accumulated surface points
});

// r,g,b in 0-255. cr,cg in [0,1] (sum with cb = 1); Y in [0,255].
export function rgbToCrCgY(r, g, b){
	const s = r + g + b;
	if(s < 1e-6) return {cr: 1 / 3, cg: 1 / 3, Y: 0};
	return {cr: r / s, cg: g / s, Y: 0.299 * r + 0.587 * g + 0.114 * b};
}

// accepts [{r,g,b}] OR [[r,g,b]] OR [{cr,cg,Y}] and normalises to {cr,cg,Y}[]
function toCcy(samples){
	const out = [];
	for(const s of samples){
		if(s == null) continue;
		if(Array.isArray(s)){ out.push(rgbToCrCgY(s[0], s[1], s[2])); }
		else if(s.cr != null && s.Y != null){ out.push({cr: s.cr, cg: s.cg, Y: s.Y}); }
		else if(s.r != null){ out.push(rgbToCrCgY(s.r, s.g, s.b)); }
	}
	return out;
}

// chromaticity is only trustworthy above a luminance floor (near black,
// R/(R+G+B) is dominated by sensor noise). Scale the chroma weight by the
// LESS reliable of the two colours being compared - near 0 for dark points,
// 1 once luminance is comfortably above the noise floor.
function chromaRel(Y, opts){ return Math.max(0, Math.min(1, (Y - 22) / opts.appChromaReliableY)); }

function modeDist(m, c, opts){
	const rel = Math.min(chromaRel(m.Y, opts), chromaRel(c.Y, opts));
	const dc = rel * Math.hypot((m.cr - c.cr), (m.cg - c.cg)) / opts.appModeGateChroma;
	const dy = Math.abs(m.Y - c.Y) / opts.appModeGateLum;
	return Math.hypot(dc, dy); // < 1 ⇒ inside the gate
}

function leaderCluster(ccy, opts){
	const modes = [];
	for(const c of ccy){
		let best = -1, bestD = Infinity;
		for(let i = 0; i < modes.length; i++){
			const d = modeDist(modes[i], c, opts);
			if(d < bestD){ bestD = d; best = i; }
		}
		if(best >= 0 && bestD < 1){
			const m = modes[best];
			m.n += 1;
			m.cr += (c.cr - m.cr) / m.n;
			m.cg += (c.cg - m.cg) / m.n;
			m.Y += (c.Y - m.Y) / m.n;
		}else if(modes.length < opts.appMaxModes){
			modes.push({cr: c.cr, cg: c.cg, Y: c.Y, n: 1});
		}else{
			// gate reached: fold into the nearest existing mode
			const m = modes[best];
			m.n += 1;
			m.cr += (c.cr - m.cr) / m.n;
			m.cg += (c.cg - m.cg) / m.n;
			m.Y += (c.Y - m.Y) / m.n;
		}
	}
	return modes;
}

// samples: colours from points with STRONG geometric + surface evidence.
export function buildAppearanceModel(samples, opts = {}){
	const o = {...APPEARANCE_DEFAULTS, ...opts};
	const ccy = toCcy(samples);
	if(ccy.length < 8) return {modes: [], totalN: ccy.length, supportedSections: 0, ok: false};

	let modes = leaderCluster(ccy, o);
	const total = ccy.length;
	// merge modes below the weight floor into the nearest survivor
	modes.sort((a, b) => b.n - a.n);
	const kept = [];
	for(const m of modes){
		if(m.n / total >= o.appMinModeWeight || kept.length === 0){ kept.push(m); continue; }
		let best = kept[0], bd = Infinity;
		for(const k of kept){ const d = modeDist(k, m, o); if(d < bd){ bd = d; best = k; } }
		const nn = best.n + m.n;
		best.cr = (best.cr * best.n + m.cr * m.n) / nn;
		best.cg = (best.cg * best.n + m.cg * m.n) / nn;
		best.Y = (best.Y * best.n + m.Y * m.n) / nn;
		best.n = nn;
	}
	for(const m of kept) m.w = m.n / total;
	kept.sort((a, b) => b.w - a.w);
	return {modes: kept, totalN: total, supportedSections: 1, ok: kept.length > 0};
}

// per-point similarity to the model in [0,1]: best match to ANY established
// mode (weight is NOT a cap here - a genuine marking mode must let a marking-
// coloured point score ~1). Tiny spurious modes are ignored via appMinModeWeight.
// `opts` may be a raw options object OR a pre-resolved one; the hot path (set
// similarity over many points) resolves it once and passes it straight through.
export function appearancePointSimilarity(model, sample, opts = APPEARANCE_DEFAULTS){
	const o = opts.__resolved ? opts : {...APPEARANCE_DEFAULTS, ...opts, __resolved: true};
	if(!model || !model.ok || !model.modes.length) return null;
	const c = Array.isArray(sample) ? rgbToCrCgY(sample[0], sample[1], sample[2])
		: (sample.cr != null ? sample : rgbToCrCgY(sample.r, sample.g, sample.b));
	const relC = chromaRel(c.Y, o);
	const invSc2 = 1 / (o.appSimSigmaChroma * o.appSimSigmaChroma);
	const invSy2 = 1 / (o.appSimSigmaLum * o.appSimSigmaLum);
	let best = 0;
	for(const m of model.modes){
		if(m.w < o.appMinModeWeight) continue;
		const rel = m._relY != null ? Math.min(m._relY, relC) : Math.min(chromaRel(m.Y, o), relC);
		const dcr = m.cr - c.cr, dcg = m.cg - c.cg, dY = m.Y - c.Y;
		const s = Math.exp(-0.5 * (rel * (dcr * dcr + dcg * dcg) * invSc2 + dY * dY * invSy2));
		if(s > best) best = s;
	}
	return best;
}

function median(a){
	if(!a.length) return 0;
	const s = [...a].sort((x, y) => x - y);
	const n = s.length;
	return n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2;
}

function resolveOpts(opts){ return opts && opts.__resolved ? opts : {...APPEARANCE_DEFAULTS, ...opts, __resolved: true}; }

// robust aggregate similarity of a set of colours to the model (median of the
// per-point similarities - markings that match a mode still count as matched).
export function appearanceSetSimilarity(model, samples, opts = {}){
	if(!model || !model.ok) return null;
	const o = resolveOpts(opts);
	const sims = [];
	for(const s of toCcy(samples)){
		const v = appearancePointSimilarity(model, s, o);
		if(v != null) sims.push(v);
	}
	return sims.length ? median(sims) : null;
}

// how DIFFERENT a colour set is from the model in [0,1]. High ⇒ these colours
// mostly do NOT match any pipe mode ⇒ appearance separates them from the pipe.
export function appearanceSeparation(model, samples, opts = {}){
	const sim = appearanceSetSimilarity(model, samples, opts);
	return sim == null ? 0 : 1 - sim;
}

// fraction of a colour set whose per-point similarity is below the match
// threshold. Unlike the median-based separation this is SENSITIVE to a
// contaminating MINORITY (soil at the pipe radius, a wrong-object arc) - used
// for "how much does colour carve a distinct surface subset out of the band".
export function appearanceMismatchFraction(model, samples, opts = {}){
	const o = resolveOpts(opts);
	if(!model || !model.ok) return 0;
	let n = 0, bad = 0;
	for(const s of toCcy(samples)){
		const v = appearancePointSimilarity(model, s, o);
		if(v == null) continue;
		n++; if(v < o.appPointMatchTau) bad++;
	}
	return n ? bad / n : 0;
}

// confidence in the model itself, [0,1].
export function appearanceModelConfidence(model, opts = {}){
	const o = {...APPEARANCE_DEFAULTS, ...opts};
	if(!model || !model.ok || !model.modes.length) return 0;
	const sec = Math.min(1, (model.supportedSections || 1) / o.appConfSectionsFull);
	const pts = Math.min(1, (model.totalN || 0) / o.appConfPointsFull);
	const dom = Math.min(1, (model.modes[0].w || 0) / 0.45); // a strong dominant mode
	return +(sec * 0.35 + pts * 0.3 + dom * 0.35).toFixed(3) * (0.5 + 0.5 * dom);
}

// conservative update from NEW strong-supported colours. Returns a NEW model
// object (input untouched). EWMA-nudges each matched mode; only spawns a new
// mode if a coherent cluster of unassigned samples recurs.
export function updateAppearanceModel(model, samples, opts = {}){
	const o = {...APPEARANCE_DEFAULTS, ...opts};
	if(!model || !model.ok) return buildAppearanceModel(samples, opts);
	const ccy = toCcy(samples);
	if(ccy.length < 8) return model;

	const modes = model.modes.map((m) => ({...m}));
	const unassigned = [];
	const acc = modes.map(() => ({cr: 0, cg: 0, Y: 0, n: 0}));
	for(const c of ccy){
		let best = -1, bd = Infinity;
		for(let i = 0; i < modes.length; i++){ const d = modeDist(modes[i], c, o); if(d < bd){ bd = d; best = i; } }
		if(best >= 0 && bd < 1){ const a = acc[best]; a.cr += c.cr; a.cg += c.cg; a.Y += c.Y; a.n++; }
		else unassigned.push(c);
	}
	for(let i = 0; i < modes.length; i++){
		if(acc[i].n < 4) continue;
		const mc = {cr: acc[i].cr / acc[i].n, cg: acc[i].cg / acc[i].n, Y: acc[i].Y / acc[i].n};
		modes[i].cr += o.appUpdateAlpha * (mc.cr - modes[i].cr);
		modes[i].cg += o.appUpdateAlpha * (mc.cg - modes[i].cg);
		modes[i].Y += o.appUpdateAlpha * (mc.Y - modes[i].Y);
		modes[i].n += acc[i].n;
	}
	// spawn at most one new mode - and ONLY while the model is still young
	// (low confidence). Once the model is established (a confident dominant
	// mode), a single off-colour section - which may be soil at the pipe
	// radius, or a marking, or a wrong-object stretch - must NOT be able to
	// add its colour to the authoritative model (spec's anti-contamination /
	// anti-feedback rule). Markings that RECUR are picked up because their
	// points are near an existing mode across many updates.
	const conf = appearanceModelConfidence({modes, totalN: model.totalN || ccy.length, supportedSections: model.supportedSections || 1, ok: true}, o);
	if(conf < 0.6 && modes.length < o.appMaxModes && unassigned.length >= Math.max(8, o.appNewModeMinFrac * ccy.length)){
		const sub = leaderCluster(unassigned, o);
		sub.sort((a, b) => b.n - a.n);
		if(sub[0] && sub[0].n >= o.appNewModeMinFrac * ccy.length){
			modes.push({cr: sub[0].cr, cg: sub[0].cg, Y: sub[0].Y, n: sub[0].n});
		}
	}
	const totalN = (model.totalN || ccy.length) + ccy.length;
	for(const m of modes) m.w = m.n / modes.reduce((s, x) => s + x.n, 0);
	modes.sort((a, b) => b.w - a.w);
	return {modes, totalN, supportedSections: (model.supportedSections || 1) + 1, ok: true};
}

// The adaptive contribution of appearance to a same-object / candidate decision.
//   strength ∈ [0,1] - how discriminative appearance is HERE (model confident
//     AND the relevant separation - vs background or vs the competing candidate
//     - is real). When two candidates share the pipe's appearance this is ~0.
//   appTerm ∈ [0,1] - the multiplicative same-object term: 1 (neutral) unless
//     strength is high and the candidate's own similarity is poor.
export function appearanceContribution({model, candidateSamples, separation, similarity, maxInfluence = 0.35, opts = {}}){
	const conf = appearanceModelConfidence(model, opts);
	if(!model || !model.ok || conf <= 0){
		return {available: false, strength: 0, appTerm: 1, similarity: null, confidence: 0};
	}
	const sim = similarity != null ? similarity
		: (separation != null && candidateSamples == null ? 1 - separation
			: appearanceSetSimilarity(model, candidateSamples, opts));
	const strength = +(conf * clamp01(separation != null ? separation : (sim == null ? 0 : 1 - sim))).toFixed(3);
	const mismatch = sim == null ? 0 : (1 - sim);
	const appTerm = +clamp01(1 - maxInfluence * strength * mismatch).toFixed(3);
	return {available: true, strength, appTerm, similarity: sim == null ? null : +sim.toFixed(3), confidence: +conf.toFixed(3)};
}

function clamp01(x){ return Math.max(0, Math.min(1, x)); }
